import React, { useState, useEffect, useRef } from 'react';
import { Plus, Minus } from 'lucide-react';
import { FAQ_DATA } from '../constants';
import gsap from 'gsap';

const FAQItem: React.FC<{ question: string; answer: string; index: number }> = ({ question, answer, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item border-b border-slate-200 dark:border-slate-800 transition-colors opacity-0 ${isOpen ? 'bg-slate-50 dark:bg-slate-900/40' : ''}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-6 px-4 md:px-8 text-left focus:outline-none group"
      >
        <div className="flex items-start gap-4 md:gap-6">
          <span className="font-mono text-[10px] text-primary mt-1.5 md:mt-2 opacity-60">
            {String(index + 1).padStart(2, '0')}
          </span>
          <h3 className={`text-base md:text-lg font-bold uppercase font-display tracking-tight transition-colors ${isOpen ? 'text-primary' : 'text-slate-900 dark:text-white group-hover:text-primary'}`}>
            {question}
          </h3>
        </div>
        <div className={`flex-shrink-0 ml-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          {isOpen ? (
            <Minus size={20} className="text-primary" />
          ) : (
            <Plus size={20} className="text-slate-400 group-hover:text-primary" />
          )}
        </div>
      </button>
      
      <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100 pb-8' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden px-4 md:px-20 lg:pr-32">
          <p className="font-mono text-xs md:text-sm text-slate-500 dark:text-slate-400 leading-relaxed whitespace-pre-wrap">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
};

const FAQ: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sectionRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            gsap.fromTo('.faq-header', { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8 });
            gsap.fromTo('.faq-item', 
              { y: 20, opacity: 0 },
              { y: 0, opacity: 1, duration: 0.6, stagger: 0.1, delay: 0.3 }
            );
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(sectionRef.current);
      return () => observer.disconnect();
    }
  }, []);

  return (
    <section ref={sectionRef} className="px-6 pt-12 pb-20 bg-white/5 dark:bg-black/5 border-t border-slate-200 dark:border-slate-800/60">
      <div className="max-w-4xl mx-auto">
        <div className="faq-header flex flex-col items-center mb-12 opacity-0">
            <div className="w-12 h-px bg-primary/30 mb-4"></div>
            <h2 className="text-3xl md:text-4xl font-bold uppercase font-display tracking-tighter text-slate-900 dark:text-white text-center">
                Frequently Asked <span className="text-primary">Questions</span>
            </h2>
            <p className="font-mono text-[10px] text-slate-500 mt-2 uppercase tracking-[0.3em]">
                Tour Support // Information
            </p>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-800">
          {FAQ_DATA.map((item, index) => (
            <FAQItem 
                key={index} 
                question={item.question} 
                answer={item.answer} 
                index={index} 
            />
          ))}
        </div>

        <div className="mt-12 p-6 border border-dashed border-slate-300 dark:border-slate-700 rounded-sm text-center">
            <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">
                Still have questions? Reach out at <a href="mailto:staroadtouring@gmail.com" className="text-primary hover:underline">staroadtouring@gmail.com</a>
            </p>
        </div>
      </div>
    </section>
  );
};

export default FAQ;